import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { useEffect, useRef, useState } from 'react'
import './SelectRefiner.scss'

const SelectRefiner = props => {
  const [isShow, setIsShow] = useState(false)
  const [singleSelectValue, setSingleSelectValue] = useState(null)
  const [multiSelectValue, setMultiSelectValue] = useState(null)
  const [rows, setRows] = useState(5)
  const clearValue = useRef(false)
  const hasSelectionChanged = useRef(false)
  const multiSelectRef = useRef(null)
  const dropdownRef = useRef(null)

  const {
    refinerLabel,
    options,
    onFilter,
    isFilterOn,
    isMultiSelect,
    addSelectedToYourFilters,
    placeHolder,
    isRefinerLoading = false,
    showMore = false,
    hasMoreOptions = false,
    onShowMore: loadMoreOptions = () => undefined,
    preserveRowsOnOptionsChange = false,
    preserveSelectedValues = false,
    multiSelectClassName = '',
    multiSelectPanelClassName = '',
    dropdownPanelClassName = '',
    panelAppendTo = null,
    closePanelOnScroll = false,
    optionItemTemplate,
    statusListRefiners = null,
    clearSelectedValues = false,
    customEmptyMessage = null,
    filterPlaceholder = null,
    refiners = null,
    refinerId = null,
  } = props

  useEffect(() => {
    if (!preserveRowsOnOptionsChange) setRows(5)
  }, [props.options, preserveRowsOnOptionsChange])

  // Body-appended panels don't follow the field on scroll, so close them instead of leaving them floating
  useEffect(() => {
    if (!panelAppendTo && !closePanelOnScroll) return

    const closeOnScroll = event => {
      const overlay = multiSelectRef.current?.getOverlay?.() ?? dropdownRef.current?.getOverlay?.()
      if (overlay && overlay.contains(event.target)) return

      multiSelectRef.current?.hide?.()
      dropdownRef.current?.hide?.()
    }

    window.addEventListener('scroll', closeOnScroll, true)
    return () => window.removeEventListener('scroll', closeOnScroll, true)
  }, [panelAppendTo, closePanelOnScroll])

  useEffect(() => {
    setMultiSelectValue(
      statusListRefiners?.map(i => {
        return {
          name: i.DisplayText,
          code: i.Value,
        }
      })
    )
  }, [statusListRefiners])

  useEffect(() => {
    clearValue.current = clearSelectedValues
    if (clearValue.current) setClearSelectedValues()

    clearValue.current = false
  }, [clearSelectedValues])

  // Keep the panel's checked state in sync when a single chip is removed from "Your filters"
  useEffect(() => {
    if (!refiners || !refinerId) return

    const selectedValues = refiners.filter(r => r.Id === refinerId).map(r => r.Value)

    if (isMultiSelect) {
      setMultiSelectValue(selectedValues.length ? selectedValues.map(name => ({ name })) : null)
    } else {
      setSingleSelectValue(selectedValues.length ? { name: selectedValues[0], code: selectedValues[0] } : null)
    }
  }, [refiners, refinerId, isMultiSelect])

  const refinerPlaceHolderTemplate = option => {
    return (
      <div className="refiner-dropdown-placeholder">
        {placeHolder ? placeHolder : '-- Click to type below --'}
      </div>
    )
  }

  const onChangeSelected = (e, isMultiSelect) => {
    if (!isMultiSelect && e.value?.isShowMore) return

    hasSelectionChanged.current = true
    isMultiSelect ? setMultiSelectValue(e.value) : setSingleSelectValue(e.value)
  }

  const onHide = isMultiSelect => {
    if (!hasSelectionChanged.current) return

    hasSelectionChanged.current = false
    isMultiSelect
      ? addSelectedToYourFilters(multiSelectValue)
      : addSelectedToYourFilters(singleSelectValue)
  }

  const onShow = isMultiSelect => {
    hasSelectionChanged.current = false

    if (refinerLabel !== 'Certificate Status' && !preserveSelectedValues) {
      setClearSelectedValues()
    }
  }

  const onShowMore = () => {
    if (isRefinerLoading) return

    if (rows + 5 >= options.length) loadMoreOptions()
    setRows(curr => curr + 5)
  }

  const showMoreTemplate = () => {
    return (
      <div className="refiners__show-more">
        <button
          disabled={isRefinerLoading}
          onClick={e => {
            // Keep the dropdown panel open and avoid selecting the sentinel row
            e.stopPropagation()
            onShowMore()
          }}
        >
          {isRefinerLoading ? 'Loading...' : 'Show more'}
        </button>
      </div>
    )
  }

  // PrimeReact 8 Dropdown has no panelFooterTemplate, so Show more is appended as a sentinel option
  const getDropdownOptions = () => {
    if (options.length === 0 || isRefinerLoading) {
      return [{ name: filterMessage, className: 'option-as-empty-message' }]
    }

    const visibleOptions = showMore ? options.slice(0, rows) : options

    if (showMore && (rows < options.length || hasMoreOptions)) {
      return [...visibleOptions, { isShowMore: true, className: 'option-as-show-more' }]
    }

    return visibleOptions
  }

  const dropdownItemTemplate = option => {
    if (option?.isShowMore) return showMoreTemplate()

    return optionItemTemplate ? optionItemTemplate(option) : option.name
  }

  const setClearSelectedValues = () => {
    isMultiSelect ? setMultiSelectValue(null) : setSingleSelectValue(null)
  }

  const filterMessage = isRefinerLoading ? (
    'Loading...'
  ) : customEmptyMessage ? (
    <span className="refiner-invalid-message">{customEmptyMessage}</span>
  ) : (
    'No available options'
  )

  return (
    <div className="ul-card -app">
      <div
        className="ul-card__item -bordered ul-card__head cursor-pointer s-refiner-height s-container-text"
        onClick={() => setIsShow(prevState => !prevState)}
      >
        <span className="font-size-14 font-weight-semi-bold">{refinerLabel}</span>
        <span className="float-right">
          <FontAwesomeIcon icon={isShow ? faCaretDown : faCaretRight} />
        </span>
      </div>

      {isShow && (
        <div className="ul-card__item ul-card__body s-select-container">
          {isMultiSelect && (
            <MultiSelect
              ref={multiSelectRef}
              panelFooterTemplate={
                showMore && (rows < options.length || hasMoreOptions) && showMoreTemplate
              }
              onHide={() => onHide(true)}
              onShow={() => onShow(true)}
              resetFilterOnHide
              value={multiSelectValue}
              options={showMore ? options.slice(0, rows) : options}
              onChange={s => onChangeSelected(s, true)}
              optionLabel="name"
              dataKey="name"
              itemTemplate={optionItemTemplate}
              placeholder={placeHolder ? placeHolder : '-- Click to type below --'}
              filter={isFilterOn}
              filterPlaceholder={filterPlaceholder}
              onFilter={onFilter}
              selectedItemTemplate={refinerPlaceHolderTemplate}
              className={`w-100 ${multiSelectClassName}`}
              panelClassName={multiSelectPanelClassName}
              appendTo={panelAppendTo}
              emptyFilterMessage={filterMessage}
            />
          )}

          {!isMultiSelect && (
            <Dropdown
              ref={dropdownRef}
              onHide={() => onHide(false)}
              onShow={() => onShow(false)}
              resetFilterOnHide
              value={singleSelectValue}
              // HACK: Dropdown filter doesn't work when the options are initially empty,
              // so we need to show at least one option to make it work,
              // hence we are showing a dummy option when the options are empty and/or loading.
              options={getDropdownOptions()}
              onChange={s => onChangeSelected(s, false)}
              optionLabel="name"
              itemTemplate={showMore ? dropdownItemTemplate : optionItemTemplate}
              placeholder={placeHolder ? placeHolder : '-- Click to type below --'}
              filter={isFilterOn}
              onFilter={onFilter}
              selectedItemTemplate={refinerPlaceHolderTemplate}
              className="w-100"
              panelClassName={dropdownPanelClassName}
              emptyFilterMessage={filterMessage}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default SelectRefiner
